/**
 * Shared price field definitions and SKU-type helpers.
 * Used by both the Pricing Manager (ProposalEditor) and the Product Editor (SkuPricingTable).
 */

export const PRICE_FIELDS = [
  {
    key: 'rrpUnit',
    label: 'RRP Unit',
    tooltip: 'Recommended retail for one single unit (e.g. one can/bottle). Use for loose single SKUs; shown on Product Database and synced to Monday/Cin7 where the format is singles.',
  },
  {
    key: 'rrpPack',
    label: 'RRP Pack',
    tooltip: 'Recommended retail for the multi-pack (e.g. 4- or 6-pack). Use when this SKU row is a pack, not singles; feeds retail listings and Kounta off-prem pack lines.',
  },
  {
    key: 'rrpCase',
    label: 'RRP Case',
    tooltip: 'Recommended retail for the full case/slab. Used for case-level RRP and related trade/retail displays.',
  },
  {
    key: 'priceOn',
    label: 'On Prem',
    tooltip: 'NZ on-premise trade price (venues, bars). Drives Cin7 on-prem tiers and Kounta cost/sell rules for venue and tap SKUs.',
  },
  {
    key: 'priceOff',
    label: 'Off Prem',
    tooltip: 'NZ off-premise trade price (retail). Used for Foodstuffs and off-prem Kounta mappings and Cin7 off-prem tiers.',
  },
  {
    key: 'priceProgressive',
    label: 'Progressive',
    tooltip: 'Progressive Enterprises trade price. Auto-calculated as Off Prem + 2.65% (six-pack slabs use Off Prem with no markup). Maps to Cin7 PriceTier10.',
  },
  {
    key: 'priceAu',
    label: 'AU',
    tooltip: 'General Australia trade/RRP-style price for this SKU where AU pricing is maintained.',
  },
  {
    key: 'priceVicSa',
    label: 'VIC/SA',
    tooltip: 'Victoria / South Australia regional price where split AU pricing is used.',
  },
  {
    key: 'priceQldWa',
    label: 'QLD/WA',
    tooltip: 'Queensland / Western Australia regional price where split AU pricing is used.',
  },
  {
    key: 'priceVenuePerLitre',
    label: 'Venue/L',
    tooltip: 'Per-litre venue price for kegs/tap (NZ). Used for Kounta keg parent and 1L takeaway sell price calculations.',
  },
  {
    key: 'pricePint',
    label: 'Pint',
    tooltip: 'Sell price for a 440ml pint pour (NZ). Kounta uses it for tap_pour_440 lines linked to this keg SKU.',
  },
  {
    key: 'priceHalfPint',
    label: 'Half Pint',
    tooltip: 'Sell price for a 285ml half-pint pour (NZ). Kounta uses it for tap_pour_285 lines linked to this keg SKU.',
  },
  {
    key: 'price150ml',
    label: '150ml',
    tooltip: 'Sell price for a 150ml tap pour / taster (NZ). Kounta uses it for tap_pour_150 lines linked to this keg SKU.',
  },
  {
    key: 'price500ml',
    label: '500ml',
    tooltip: 'Sell price for a 500ml tap pour (NZ). Kounta uses it for tap_pour_500 lines linked to this keg SKU.',
  },
  {
    key: 'priceBar',
    label: 'O.P.',
    tooltip: 'On-premise single (O.P.) sell price for cans/bottles at the bar (NZ). Kounta uses it for on_prem_single lines.',
  },
];

export const PRICE_FIELD_KEYS = PRICE_FIELDS.map((pf) => pf.key);

/**
 * Multilevel headers for the Product Editor pricing summary table.
 * RRP, venue pours, and O.P. are consumer-style prices (incl. GST in NZ). NZ trade
 * (on/off/progressive) is excl. GST. AU columns are B2B trade in AUD (excl. GST).
 * See marginUtils (retail/pour inc GST) and kegPricingSuggestions (pours inc, On Prem ex).
 */
export const PRICING_SUMMARY_COLUMN_GROUPS = [
  { title: 'Incl. GST', subtitle: 'RRP', keys: ['rrpUnit', 'rrpPack', 'rrpCase'] },
  { title: 'Excl. GST', subtitle: 'NZ trade', keys: ['priceOn', 'priceOff', 'priceProgressive'] },
  { title: 'Excl. GST', subtitle: 'AU', keys: ['priceAu', 'priceVicSa', 'priceQldWa'] },
  {
    title: 'Incl. GST',
    subtitle: 'Venue & on-premise',
    keys: ['priceVenuePerLitre', 'pricePint', 'priceHalfPint', 'price150ml', 'price500ml', 'priceBar'],
  },
];

export const CAN_BOTTLE_POUR_ONLY_KEYS = new Set([
  'priceVenuePerLitre', 'pricePint', 'priceHalfPint', 'price150ml', 'price500ml',
]);

export const KEG_NON_APPLICABLE_KEYS = new Set([
  'rrpPack', 'rrpCase',
]);

export function skuContainerType(sku) {
  return String(sku?.containerType ?? sku?.container_type ?? '').toLowerCase();
}

export function isKegSku(sku) {
  return skuContainerType(sku) === 'keg';
}

export function isPackagedCanOrBottleSku(sku) {
  const ct = skuContainerType(sku);
  return ct === 'can' || ct === 'bottle';
}

export function isLooseSku(sku) {
  const pt = String(sku?.packType ?? sku?.pack_type ?? '').trim();
  return !pt || pt === '1';
}

export function isPackSku(sku) {
  const pt = String(sku?.packType ?? sku?.pack_type ?? '').trim();
  return Boolean(pt && pt !== '1');
}

/** Off Prem + 2.65%; six-pack slabs (case_type 4x6) use Off Prem with no markup. Kegs: null. */
export function calculateProgressivePrice(priceOff, sku) {
  if (!sku || isKegSku(sku)) return null;
  const off = parseFloat(String(priceOff));
  if (Number.isNaN(off) || off <= 0) return null;
  const ct = String(sku?.caseType ?? sku?.case_type ?? '');
  if (ct === '4x6') return off;
  return off * 1.0265;
}

export function priceFieldGreyOut(sku, fieldKey) {
  if (!sku) return false;
  if (isKegSku(sku) && KEG_NON_APPLICABLE_KEYS.has(fieldKey)) return true;
  if (isKegSku(sku) && fieldKey === 'priceOff') return true;
  if (isKegSku(sku) && fieldKey === 'priceProgressive') return true;
  if (isKegSku(sku) && fieldKey === 'priceBar') return true;
  if (isPackagedCanOrBottleSku(sku) && CAN_BOTTLE_POUR_ONLY_KEYS.has(fieldKey)) return true;
  if (!isKegSku(sku)) {
    if (isPackSku(sku) && fieldKey === 'rrpUnit') return true;
    if (isLooseSku(sku) && fieldKey === 'rrpPack') return true;
  }
  return false;
}

export function priceFieldGreyOutTooltip(sku, fieldKey) {
  if (!sku) return '';
  if (isKegSku(sku) && KEG_NON_APPLICABLE_KEYS.has(fieldKey)) {
    return 'Keg SKUs do not use pack/case RRP.';
  }
  if (isKegSku(sku) && fieldKey === 'priceOff') {
    return 'Kegs use the same price on and off premise. The On Prem value is used for both.';
  }
  if (isKegSku(sku) && fieldKey === 'priceProgressive') {
    return 'Progressive pricing does not apply to keg SKUs.';
  }
  if (isKegSku(sku) && fieldKey === 'priceBar') {
    return 'O.P. price applies to packaged can or bottle SKUs sold on-premise, not keg SKUs.';
  }
  if (isPackagedCanOrBottleSku(sku) && CAN_BOTTLE_POUR_ONLY_KEYS.has(fieldKey)) {
    return 'Pour-size venue fields do not apply to packaged can or bottle SKUs.';
  }
  if (!isKegSku(sku)) {
    if (isPackSku(sku) && fieldKey === 'rrpUnit') {
      return 'This row is a pack SKU — use RRP Pack, not RRP Unit.';
    }
    if (isLooseSku(sku) && fieldKey === 'rrpPack') {
      return 'This row is a loose single SKU — use RRP Unit, not RRP Pack.';
    }
  }
  return '';
}

/**
 * Price column keys to hide when Pricing Proposals "SKU focus" is active.
 * Aligns with container-type rules in priceFieldGreyOut (keg vs can/bottle only).
 *
 * @param {'keg'|'can'|'bottle'|null|undefined} containerCategory
 * @returns {Set<string>}
 */
export function priceFieldKeysHiddenForSkuFocus(containerCategory) {
  if (containerCategory === 'keg') {
    return new Set([...KEG_NON_APPLICABLE_KEYS, 'priceOff', 'priceProgressive', 'priceBar']);
  }
  if (containerCategory === 'can' || containerCategory === 'bottle') {
    return new Set(CAN_BOTTLE_POUR_ONLY_KEYS);
  }
  return new Set();
}

/**
 * Extract just the price fields from a SKU object into a flat map.
 * Useful for snapshotting "saved" prices for old-vs-new comparison.
 */
export function extractPriceFields(sku) {
  if (!sku) return {};
  const out = {};
  PRICE_FIELD_KEYS.forEach((k) => {
    if (sku[k] != null && sku[k] !== '') {
      out[k] = sku[k];
    }
  });
  return out;
}
